import * as React from 'react';

import type { ComposeMode } from '../../mail/-components/types';
import type { Id } from '@minty/shared/ids';
import type { ReplyTarget } from '~/components/message-composer';

// Derived from the composer's own prop type rather than hand-mirrored, so the
// two cannot drift. The task composer additionally carries the subject.
export type ComposerReplyTarget = ReplyTarget & { subject: string };

/** A forward is opened from one message in the feed, so it carries only which
 *  one. Deliberately not a `ComposerReplyTarget`: outbound rows have no reply
 *  sets at all, and they are as forwardable as inbound ones. */
export type ComposerForwardTarget = { emailId: ReplyTarget['emailId'] };

/** A mailbox email; the mail server addresses whatever is sent about it. */
export type MailboxComposerTarget = {
  emailId: Id<'mailboxEmails'>;
  subject: string;
  fromEmail: string;
};

export type ComposerView =
  | { view: 'collapsed' }
  | { view: 'mail'; mode: 'new' }
  | {
      view: 'mail';
      mode: Exclude<ComposeMode, 'new'>;
      target: MailboxComposerTarget;
    }
  | { view: 'email'; mode: 'forward'; target: ComposerForwardTarget }
  | { view: 'email'; mode: 'new' }
  | { view: 'email'; mode: 'reply'; target: ComposerReplyTarget }
  | { view: 'sms' };

/** What replying to the contact's newest email opens, in whichever mailbox
 *  the deployment uses. */
export type ComposerReplyView =
  | { view: 'email'; mode: 'reply'; target: ComposerReplyTarget }
  | { view: 'mail'; mode: 'reply'; target: MailboxComposerTarget };

type ComposerStateContextValue = {
  state: ComposerView;
  setState: (state: ComposerView) => void;
};

const ComposerStateContext =
  React.createContext<ComposerStateContextValue | null>(null);

export function ComposerStateProvider({
  children,
  initialState,
}: {
  children: React.ReactNode;
  initialState: ComposerView;
}) {
  const [state, setState] = React.useState<ComposerView>(initialState);
  const value = React.useMemo(() => ({ state, setState }), [state]);

  return (
    <ComposerStateContext.Provider value={value}>
      {children}
    </ComposerStateContext.Provider>
  );
}

export function useComposerState() {
  const context = React.useContext(ComposerStateContext);

  if (!context) {
    throw new Error(
      'useComposerState must be used within a ComposerStateProvider',
    );
  }

  return context;
}
